import React, { useState, useEffect } from 'react';
import { Card, Button, Table, Spin, message } from 'antd';
import { ReloadOutlined, DownloadOutlined } from '@ant-design/icons';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import apiClient from '../../../utils/apiClient';
import { downloadCSV } from '../../../utils/csvExport';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface CouponComparisonData {
  id: number;
  coupon_name: string;
  discount_type: string;
  discount_value: number;
  usage_count: number;
  avg_before_discount: number;
  avg_after_discount: number;
  avg_discount_amount: number;
  total_before_discount: number;
  total_after_discount: number;
}

const CouponAverageComparison: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<CouponComparisonData[]>([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/coupon-analytics/analytics/coupon-average-comparison');

      if (response.data.success) {
        setData(response.data.data);
      } else {
        message.error('データ取得に失敗しました');
      }
    } catch (error) {
      console.error('クーポン別平均購入単価比較エラー:', error);
      message.error('データ取得エラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleExportCSV = () => {
    const csvData = data.map(row => ({
      'クーポン名': row.coupon_name,
      '使用回数': row.usage_count,
      '値引き前平均': `¥${Math.round(row.avg_before_discount).toLocaleString()}`,
      '値引き後平均': `¥${Math.round(row.avg_after_discount).toLocaleString()}`,
      '平均値引き額': `¥${Math.round(row.avg_discount_amount).toLocaleString()}`,
      '値引き前合計': `¥${Math.round(row.total_before_discount).toLocaleString()}`,
      '値引き後合計': `¥${Math.round(row.total_after_discount).toLocaleString()}`
    }));

    downloadCSV(csvData, 'クーポン別平均購入単価比較.csv');
    message.success('CSVファイルをダウンロードしました');
  };

  // 棒グラフデータ
  const chartData = {
    labels: data.map(d => d.coupon_name),
    datasets: [
      {
        label: '値引き前平均',
        data: data.map(d => Math.round(d.avg_before_discount)),
        backgroundColor: 'rgba(255, 99, 132, 0.6)',
        borderColor: 'rgba(255, 99, 132, 1)',
        borderWidth: 1
      },
      {
        label: '値引き後平均',
        data: data.map(d => Math.round(d.avg_after_discount)),
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: '金額（円）'
        }
      }
    }
  };

  const columns = [
    {
      title: 'クーポン名',
      dataIndex: 'coupon_name',
      key: 'coupon_name',
      ellipsis: true,
      width: 200
    },
    {
      title: '使用回数',
      dataIndex: 'usage_count',
      key: 'usage_count',
      align: 'right' as const,
      render: (count: number) => `${count}回`,
      sorter: (a: CouponComparisonData, b: CouponComparisonData) => a.usage_count - b.usage_count,
      width: 120
    },
    {
      title: '値引き前平均',
      dataIndex: 'avg_before_discount',
      key: 'avg_before_discount',
      align: 'right' as const,
      render: (amount: number) => `¥${Math.round(amount).toLocaleString()}`,
      width: 140
    },
    {
      title: '値引き後平均',
      dataIndex: 'avg_after_discount',
      key: 'avg_after_discount',
      align: 'right' as const,
      render: (amount: number) => (
        <span className="font-bold text-green-600">
          ¥{Math.round(amount).toLocaleString()}
        </span>
      ),
      sorter: (a: CouponComparisonData, b: CouponComparisonData) =>
        a.avg_after_discount - b.avg_after_discount,
      width: 140
    },
    {
      title: '平均値引き額',
      dataIndex: 'avg_discount_amount',
      key: 'avg_discount_amount',
      align: 'right' as const,
      render: (amount: number) => (
        <span className="text-red-600">
          ¥{Math.round(amount).toLocaleString()}
        </span>
      ),
      width: 140
    },
    {
      title: '客単価向上効果',
      key: 'uplift',
      align: 'right' as const,
      render: (_: any, record: CouponComparisonData) => {
        const diff = record.avg_after_discount - record.avg_discount_amount - record.avg_after_discount;
        const isPositive = diff > 0;
        return (
          <span className={isPositive ? 'text-green-600 font-bold' : 'text-gray-500'}>
            {isPositive ? '↗' : '→'} {record.avg_after_discount > 0 ? '有効' : '-'}
          </span>
        );
      },
      width: 120
    }
  ];

  return (
    <div>
      {/* アクションボタン */}
      <Card className="mb-4">
        <div className="flex justify-end gap-2">
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchData}
            loading={loading}
          >
            更新
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExportCSV}
            disabled={data.length === 0}
          >
            CSV出力
          </Button>
        </div>
      </Card>

      {/* 説明カード */}
      <Card className="mb-4 bg-blue-50">
        <div className="flex items-start gap-3">
          <div className="text-2xl">💡</div>
          <div>
            <div className="font-bold text-gray-800 mb-1">クーポン別平均購入単価比較とは？</div>
            <div className="text-sm text-gray-600">
              各クーポンの使用時に、値引き前後の平均購入金額を比較します。
              クーポンが客単価の向上に貢献しているかを評価できます。
            </div>
          </div>
        </div>
      </Card>

      {loading ? (
        <Card>
          <div className="flex justify-center items-center" style={{ minHeight: 400 }}>
            <Spin size="large" />
          </div>
        </Card>
      ) : (
        <>
          {/* 棒グラフ */}
          <Card title="クーポン別平均購入単価比較（グラフ）" className="mb-4">
            {data.length > 0 ? (
              <Bar data={chartData} options={chartOptions} />
            ) : (
              <div className="text-center text-gray-500 py-12">
                データがありません
              </div>
            )}
          </Card>

          {/* データテーブル */}
          <Card title="クーポン別平均購入単価比較（詳細）">
            <Table
              columns={columns}
              dataSource={data}
              rowKey="id"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `全 ${total} 件`
              }}
              locale={{
                emptyText: '該当するデータがありません'
              }}
              scroll={{ x: 1000 }}
            />
          </Card>
        </>
      )}
    </div>
  );
};

export default CouponAverageComparison;
